import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Button,
    Checkbox,
    FormControlLabel,
    Alert,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircleIcon from '@mui/icons-material/Circle';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const AICompetitionPreview = ({
    competition,
    duplicateCheck,
    selected,
    onToggleSelect,
    onDuplicateFound,
    onPreview,
    resolutionData,
    onResetDecision
}) => {
    const [expanded, setExpanded] = React.useState(false);
    const [mergeStrategy, setMergeStrategy] = React.useState('create'); // 'create', 'merge', 'skip'
    const [selectedFields, setSelectedFields] = React.useState([
        'memo', 'subject', 'eligibility_memo', 'test_format_memo', 'url', 'events'
    ]);

    const details = competition.competition_details;
    const isDuplicate = duplicateCheck?.exists;
    const isResolved = resolutionData?.resolved || false;
    const resolutionAction = resolutionData?.action; // 'skip', 'merge', 'create'

    // Determine if checkbox should be disabled
    // Disable if: unresolved duplicate, or action is skip/merge
    const isCheckboxDisabled = (isDuplicate && !isResolved) || resolutionAction === 'skip' || resolutionAction === 'merge';

    const handleToggleSelect = () => {
        if (!isCheckboxDisabled) {
            onToggleSelect(!selected, mergeStrategy, selectedFields);
        }
    };

    const handlePreviewClick = () => {
        // If duplicate exists and not resolved, force user to resolve it first
        if (isDuplicate && !isResolved) {
            onDuplicateFound(duplicateCheck);
        } else if (onPreview) {
            // No duplicate or duplicate resolved - show preview
            onPreview(competition);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Date TBD';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Determine card styling based on state
    const getCardStyle = () => {
        if (selected) {
            return {
                borderLeft: '4px solid #1976d2',
                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                opacity: 1
            };
        }

        if (resolutionAction === 'skip') {
            return {
                borderLeft: '4px solid #9e9e9e',
                backgroundColor: 'rgba(158, 158, 158, 0.05)',
                opacity: 0.7
            };
        }

        if (resolutionAction === 'merge') {
            return {
                borderLeft: '4px solid #2e7d32',
                backgroundColor: 'rgba(46, 125, 50, 0.05)',
                opacity: 0.85
            };
        }

        if (isDuplicate && !isResolved) {
            return {
                borderLeft: '4px solid #ed6c02',
                backgroundColor: 'rgba(237, 108, 2, 0.05)',
                opacity: 0.85
            };
        }

        return {
            borderLeft: '4px solid transparent',
            backgroundColor: 'inherit',
            opacity: 1
        };
    };

    return (
        <Card
            variant="outlined"
            sx={getCardStyle()}
        >
            <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h6" sx={{ mb: 0 }}>
                                {details?.name || competition.title}
                            </Typography>

                            {/* Action Badge */}
                            {resolutionAction === 'skip' && (
                                <Chip
                                    label="Skipped"
                                    size="small"
                                    color="default"
                                    sx={{ bgcolor: '#9e9e9e', color: 'white' }}
                                />
                            )}
                            {resolutionAction === 'merge' && (
                                <Chip
                                    label="Merged"
                                    size="small"
                                    color="success"
                                />
                            )}
                            {resolutionAction === 'create' && (
                                <Chip
                                    label="Create New"
                                    size="small"
                                    color="info"
                                />
                            )}

                            {/* Preview Button */}
                            {onPreview && (
                                <Tooltip
                                    title={
                                        isDuplicate && !isResolved
                                            ? "Resolve duplicate first"
                                            : "Preview competition details"
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={handlePreviewClick}
                                        color={isDuplicate && !isResolved ? "warning" : "primary"}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {/* Reset Button */}
                            {resolutionAction && onResetDecision && (
                                <Tooltip title="Reset decision" arrow>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<RestartAltIcon />}
                                        onClick={onResetDecision}
                                        color="inherit"
                                    >
                                        Reset
                                    </Button>
                                </Tooltip>
                            )}
                        </Box>

                        {/* Type Badge and Subject Chips */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            <Chip
                                label={details?.competition_type === 'test' ? 'Test' : 'Competition'}
                                color={details?.competition_type === 'test' ? 'primary' : 'success'}
                                size="small"
                            />
                            {details?.subject?.map((subj, idx) => (
                                <Chip
                                    key={idx}
                                    label={subj}
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                        </Box>

                        {/* Duplicate Status */}
                        {duplicateCheck && (
                            <Box sx={{ mt: 1 }}>
                                {isDuplicate ? (
                                    <Alert
                                        severity="warning"
                                        icon={<WarningIcon />}
                                        action={
                                            <Button
                                                size="small"
                                                onClick={() => onDuplicateFound(duplicateCheck)}
                                            >
                                                Review
                                            </Button>
                                        }
                                    >
                                        Possible duplicate found: {duplicateCheck.competition?.name}
                                        {' '}({duplicateCheck.match_type}, {Math.round(duplicateCheck.similarity_score * 100)}% match)
                                    </Alert>
                                ) : (
                                    <Alert severity="success" icon={<CheckCircleIcon />}>
                                        New competition - no duplicates found
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </Box>

                    <Tooltip
                        title={
                            isCheckboxDisabled
                                ? resolutionAction === 'skip'
                                    ? "Skipped - cannot save"
                                    : resolutionAction === 'merge'
                                        ? "Already merged to database"
                                        : "Resolve duplicate first"
                                : ""
                        }
                        arrow
                    >
                        <span>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selected}
                                        onChange={handleToggleSelect}
                                        color="primary"
                                        disabled={isCheckboxDisabled}
                                    />
                                }
                                label="Save"
                                disabled={isCheckboxDisabled}
                            />
                        </span>
                    </Tooltip>
                </Box>

                {/* Description */}
                <Typography variant="body2" color="text.secondary" paragraph>
                    {details?.memo || competition.description}
                </Typography>

                {/* URL */}
                {details?.url && (
                    <Button
                        variant='outlined'
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        href={details.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mb: 1, mr: 2 }}
                    >
                        Official Website
                    </Button>
                )}

                {/* Expand/Collapse Button */}
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                    endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mb: 1 }}
                >
                    {expanded ? 'Show Less' : 'Show More Details'}
                </Button>

                {/* Expanded Details */}
                <Collapse in={expanded}>
                    <Box sx={{ mt: 2 }}>
                        {/* Eligibility */}
                        {details?.eligibility_memo && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Eligibility
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {details.eligibility_memo}
                                </Typography>
                            </Box>
                        )}

                        {/* Test Format */}
                        {details?.test_format_memo && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Format
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {details.test_format_memo}
                                </Typography>
                            </Box>
                        )}

                        {/* Events */}
                        {details?.events && details.events.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Important Dates
                                </Typography>
                                <List dense>
                                    {details.events.map((event, idx) => (
                                        <ListItem key={idx} disablePadding>
                                            <CircleIcon
                                                sx={{
                                                    color: event.color,
                                                    fontSize: 12,
                                                    mr: 1
                                                }}
                                            />
                                            <ListItemText
                                                primary={event.name}
                                                secondary={formatDate(event.date)}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}

                        {/* Bullet Points from AI */}
                        {competition.bullet_points && competition.bullet_points.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Key Information
                                </Typography>
                                <List dense>
                                    {competition.bullet_points.map((bullet, idx) => (
                                        <ListItem key={idx} disablePadding>
                                            <Typography variant="body2" color="text.secondary">
                                                • {bullet}
                                            </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default AICompetitionPreview;
